import { useEffect, useMemo, useState } from 'react';

import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { AgGridReact } from 'ag-grid-react';
import { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useCreateNewDonorMutation } from '@/app/services/donorSchedules';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { Button as UiButton } from '@/components/ui/button';
import { Checkbox as UiCheckbox } from '@/components/ui/checkbox';
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Dialog as UiDialog,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useFormNav } from '@/hooks/useFormNav';
import { useRevisionIncrement } from '@/hooks/useRevisionIncrement';
import { ToastLife, useToast } from '@/services/toast';
import { UPDATE_REVISIONS } from '@/types.gql';
import { appendRevisionComment } from '@/utils/formatting';

import { AccountAutoComplete } from '../account-auto-complete/AccountAutoComplete';
import { BreedsAutoComplete } from '../breeds-auto-complete/BreedsAutoComplete';
import { HousingAutoComplete } from '../housing-autocomplete';
import { IvfStatusAutoComplete } from '../ivf-status-autocomplete/IvfStatusAutocomplete';
import { SpeciesDropdown } from '../species-dropdown/SpeciesDropdown';
import { YardsAutoComplete } from '../yards-auto-complete/YardsAutoComplete';
import {
  createDonorFormSchema,
  DONOR_FIELD_MAX_LENGTHS,
  DonorFormValues,
  DonorLimitedField,
  getDonorNameMaxLength,
  getMaxLengthMessage,
} from './schema';

interface INewDonorDialogProps {
  visible: boolean;
  onHide: () => void;
  initialValues?: Partial<DonorFormValues>;
  gridRef?: React.RefObject<AgGridReact<any>>;
  revisionComment: string;
  setRevisionComment: (revisionComment: string) => void;
  revisionNumber: {
    previousRevision: string;
    currentRevision: string;
  };
  setRevisionNumber: (revisionNumber: {
    previousRevision: string;
    currentRevision: string;
  }) => void;
  updateRevisionsAfterSave?: boolean;
}

export const NewDonorDialog = ({
  visible,
  onHide,
  gridRef,
  initialValues,
  revisionComment,
  setRevisionComment,
  revisionNumber,
  setRevisionNumber,
  updateRevisionsAfterSave,
}: INewDonorDialogProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [createDonor, { isLoading: isCreatingDonor }] =
    useCreateNewDonorMutation();
  const [updateRevisions] = useMutation(UPDATE_REVISIONS);
  const { incrementRevisionWithTopRevisions } = useRevisionIncrement();
  const noParenthesesForBlankDonorName = useFeatureFlag(
    'New_Donor_Does_Not_Get_Parentheses',
  );
  const characterLimitsEnabled = useFeatureFlag('DS_Donor_Char_Limits');
  const isBaseUi = useFeatureFlag('new_donor_prime_to_base_ui');
  const [baseDialogContainer, setBaseDialogContainer] =
    useState<HTMLElement | null>(null);
  const [selectedSubFamilies, setSelectedSubFamilies] = useState<number[]>([]);

  const {
    ref: formNavRef,
    onKeyDownCapture,
    selectOnFocus,
    handleEnterKeyAdvance,
  } = useFormNav<HTMLFormElement>({
    selectors: [
      'input:not([type="hidden"]):not([disabled]):not([readonly])',
      'select:not([disabled])',
      '.p-dropdown:not(.p-disabled) input',
      '.p-dropdown:not(.p-disabled) input[role="combobox"]',
      '.p-dropdown:not(.p-disabled) .p-dropdown-label',
      '.p-autocomplete:not(.p-disabled) input',
      '.p-calendar:not(.p-disabled) input',
      '.p-inputmask:not(.p-disabled) input',
      '.p-inputnumber:not(.p-disabled) input',
      '.p-selectbutton:not(.p-disabled) .p-button:first-child',
      '.p-nucleus:not(.p-disabled) .p-button:first-child',
      'textarea:not([disabled]):not([readonly])',
      'button:not([disabled])[tabindex]',
    ],
    wrap: true,
    sortByTabIndex: true,
    skipAmount: 3,
    allowDropdownEnterSelect: true,
  });

  const [params] = useSearchParams();
  const isRevisionMode = params.get('revision') === 'true';

  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const container = formNavRef.current;
      const target = e.target as HTMLElement | null;

      if (!container || !target || !container.contains(target)) return;

      // Skip base-ui Select trigger/popup so it can handle its own keyboard nav.
      if (
        target.closest('[data-slot="select-trigger"]') ||
        target.closest('[data-slot="select-content"]') ||
        target.closest('[role="listbox"]') ||
        target.closest('[role="option"]')
      ) {
        return;
      }

      if (e.key === 'Enter') {
        handleEnterKeyAdvance(e as React.KeyboardEvent<HTMLElement>);
      } else {
        onKeyDownCapture(e as React.KeyboardEvent<HTMLElement>);
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [handleEnterKeyAdvance, onKeyDownCapture, formNavRef, visible]);

  const resolverSchema = useMemo(
    () => createDonorFormSchema(t, characterLimitsEnabled),
    [t, characterLimitsEnabled],
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<DonorFormValues>({
    resolver: zodResolver(resolverSchema),
    defaultValues: {
      donorName: undefined,
      breedId: undefined,
      pedName: undefined,
      metalTag: undefined,
      lastActivity: undefined,
      tattoo: undefined,
      regPrefix: undefined,
      birth: undefined,
      ownerId: undefined,
      yardId: undefined,
      registrationNumber: undefined,
      received: undefined,
      pregnant: false,
      ivfStatusId: undefined,
      exportDonor: false,
      housingId: undefined,
      ...initialValues,
    },
  });

  const watchedValues = watch();

  const getFieldMaxLength = (name: keyof DonorFormValues) => {
    if (!characterLimitsEnabled) return undefined;
    if (name === 'donorName')
      return getDonorNameMaxLength(watchedValues.tattoo);
    return DONOR_FIELD_MAX_LENGTHS[name as DonorLimitedField];
  };

  const isOverCharacterLimit = (name: keyof DonorFormValues) => {
    const max = getFieldMaxLength(name);
    return max !== undefined && String(watchedValues[name] ?? '').length > max;
  };

  const getFormErrorMessage = (name: keyof DonorFormValues) => {
    return (
      errors[name] && (
        <small className="mt-1 block text-[0.87rem] text-[var(--tog-red)]">
          {errors[name]?.message}
        </small>
      )
    );
  };

  const getFieldFeedback = (name: keyof DonorFormValues) => {
    const max = getFieldMaxLength(name);
    if (max === undefined) return getFormErrorMessage(name);

    const length = String(watchedValues[name] ?? '').length;
    const overLimit = length > max;

    const message = overLimit
      ? getMaxLengthMessage(t, name as DonorLimitedField, max)
      : errors[name]?.message;

    return (
      <div className="mt-1 flex items-start justify-between gap-2">
        <small className="text-[0.87rem] text-[var(--tog-red)]">
          {overLimit && (
            <i
              className="pi pi-exclamation-circle mr-1"
              data-testid={`${name}-over-limit-icon`}
              aria-label={t('donor.characterLimitExceededAria')}
            />
          )}
          {message}
        </small>
        <small
          className={classNames('shrink-0 text-[0.87rem]', {
            'font-semibold text-[var(--tog-red)]': overLimit,
            'text-muted-foreground': !overLimit,
          })}
          data-testid={`${name}-character-count`}
        >
          {length}/{max}
        </small>
      </div>
    );
  };

  const onInvalid = () => {
    if (!characterLimitsEnabled) return;
    toast.show({
      severity: 'error',
      summary: t('common.validationError'),
      detail: t('common.pleaseFixErrors'),
      life: ToastLife.MEDIUM,
    });
  };

  const onSave = async (input: DonorFormValues) => {
    if (isBaseUi && isCreatingDonor) return;
    try {
      const result = await createDonor({
        input: {
          ...input,
          ...(noParenthesesForBlankDonorName
            ? {
                donorName: input.donorName?.trim()
                  ? input.donorName.trim()
                  : null,
                tattoo: input.tattoo?.trim() ? input.tattoo.trim() : null,
              }
            : {}),
          pregnant: input.pregnant ? 100000001 : 100000000,
          exportDonor: input.exportDonor ? 100000000 : null,
        },
      }).unwrap();

      if (characterLimitsEnabled && result?.createNewDonor?.success === false) {
        toast.show({
          severity: 'error',
          summary: t('donor.failedToCreateDonor'),
          detail: result.createNewDonor.message || t('common.pleaseFixErrors'),
          life: ToastLife.LONG,
        });
        return;
      }

      const newNoteText = `Input Donor, ${input.donorName}`;
      if (params.get('revision') === 'true') {
        setRevisionComment(appendRevisionComment(revisionComment, newNoteText));
        setRevisionNumber(
          incrementRevisionWithTopRevisions(
            revisionNumber.previousRevision,
            revisionNumber.currentRevision,
          ),
        );
      }

      if (updateRevisionsAfterSave) {
        updateRevisions({
          variables: {
            updates: {
              donorScheduleId:
                gridRef?.current?.api?.getSelectedNodes()?.[0]?.data
                  .donorScheduleId,
              revisionComment: newNoteText,
            },
          },
        });
      }
      gridRef?.current?.api.refreshServerSide();
      gridRef?.current?.api.redrawRows();

      reset();
      onHide();
    } catch (error) {
      console.error('Failed to create donor:', error);
      if (characterLimitsEnabled) {
        toast.show({
          severity: 'error',
          summary: t('donor.failedToCreateDonor'),
          detail:
            error instanceof Error ? error.message : t('common.unknownError'),
          life: ToastLife.LONG,
        });
      }
    }
  };

  const footerContent = (
    <>
      <Button
        label={t('common.cancel')}
        icon="pi pi-times"
        severity="secondary"
        onClick={() => {
          reset();
          onHide();
        }}
      />
      <Button
        label={t('common.save')}
        icon="pi pi-check"
        onClick={handleSubmit(onSave, onInvalid)}
        className={isRevisionMode ? 'border-yellow-500 bg-yellow-500' : ''}
        tooltip={isRevisionMode ? t('revision.revisionFieldChangeTooltip') : ''}
      />
    </>
  );

  if (isBaseUi) {
    return (
      <UiDialog
        open={visible}
        onOpenChange={(open) => {
          if (!open) onHide();
        }}
      >
        <DialogContent
          preflight={false}
          ref={setBaseDialogContainer}
          className="z-[2100] md:max-w-[1200px]"
          overlayClassName="z-[2100]"
        >
          <DialogHeader className="preflight">
            <DialogTitle>{t('donor.newDonor')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="preflight">
            <form
              onSubmit={handleSubmit(onSave, onInvalid)}
              className="grid grid-cols-3 gap-4 p-4 [&_.p-calendar]:w-full [&_.p-checkbox]:mt-2 [&_.p-dropdown]:w-full [&_.p-inputtext]:w-full [&_[data-slot='field']]:m-0 [&_[data-slot='field']]:w-full"
              ref={formNavRef}
            >
              <div className="label-input-group">
                <label htmlFor="donorName">{t('donor.donorName')}</label>
                <Controller
                  name="donorName"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      id={field.name}
                      value={field.value}
                      aria-invalid={
                        fieldState.invalid || isOverCharacterLimit('donorName')
                      }
                      onChange={(e) => field.onChange(e.target.value)}
                      tabIndex={1}
                    />
                  )}
                />
                {getFieldFeedback('donorName')}
              </div>

              <SpeciesDropdown
                label={t('fields.species', 'Species')}
                tabIndex={2}
                onChange={(subFamilies) => {
                  setSelectedSubFamilies(subFamilies);
                  setValue('breedId', '');
                }}
              />

              <div className="label-input-group">
                <Controller
                  name="breedId"
                  control={control}
                  render={({ field }) => (
                    <BreedsAutoComplete
                      key={selectedSubFamilies.join(',')}
                      label={t('fields.breedRequired')}
                      subFamilies={selectedSubFamilies}
                      onSelect={(e: AutoCompleteSelectEvent) => {
                        field.onChange(e.value?.id);
                      }}
                      onChange={(e) => {
                        const value = e.value as unknown;
                        if (
                          typeof value === 'object' &&
                          value !== null &&
                          'id' in value &&
                          typeof value.id === 'string' &&
                          value.id.length > 0
                        ) {
                          field.onChange(value.id);
                          return;
                        }
                        field.onChange(undefined);
                      }}
                      tabIndex={3}
                    />
                  )}
                />
                {getFieldFeedback('breedId')}
              </div>

              <div className="label-input-group">
                <label htmlFor="pedName">{t('donor.pedigreeName')}</label>
                <Controller
                  name="pedName"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id={field.name}
                      value={field.value}
                      aria-invalid={isOverCharacterLimit('pedName')}
                      onChange={(e) => field.onChange(e.target.value)}
                      tabIndex={4}
                    />
                  )}
                />
                {getFieldFeedback('pedName')}
              </div>

              <div className="label-input-group">
                <label htmlFor="metalTag">{t('donor.metalTag')}</label>
                <Controller
                  name="metalTag"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id={field.name}
                      value={field.value}
                      aria-invalid={isOverCharacterLimit('metalTag')}
                      onChange={(e) => field.onChange(e.target.value)}
                      tabIndex={5}
                    />
                  )}
                />
                {getFieldFeedback('metalTag')}
              </div>

              <div className="label-input-group">
                <label htmlFor="lastActivity">{t('donor.lastActivity')}</label>
                <Controller
                  name="lastActivity"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id={field.name}
                      value={field.value}
                      aria-invalid={isOverCharacterLimit('lastActivity')}
                      onChange={(e) => field.onChange(e.target.value)}
                      tabIndex={6}
                    />
                  )}
                />
                {getFieldFeedback('lastActivity')}
              </div>

              <div className="label-input-group">
                <label htmlFor="tattoo">{t('donor.earTag')}</label>
                <Controller
                  name="tattoo"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id={field.name}
                      value={field.value}
                      aria-invalid={isOverCharacterLimit('tattoo')}
                      onChange={(e) => field.onChange(e.target.value)}
                      tabIndex={7}
                    />
                  )}
                />
                {getFieldFeedback('tattoo')}
              </div>

              <div className="label-input-group">
                <label htmlFor="regPrefix">{t('fields.country')}</label>
                <Controller
                  name="regPrefix"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id={field.name}
                      value={field.value}
                      aria-invalid={isOverCharacterLimit('regPrefix')}
                      onChange={(e) => field.onChange(e.target.value)}
                      tabIndex={8}
                    />
                  )}
                />
                {getFieldFeedback('regPrefix')}
              </div>

              <div className="label-input-group">
                <label htmlFor="birth">{t('fields.birthDate')}</label>
                <Controller
                  name="birth"
                  control={control}
                  render={({ field }) => (
                    <Calendar
                      id={field.name}
                      value={field.value}
                      onChange={(e) => field.onChange(e.value)}
                      showIcon
                      dateFormat="mm/dd/yy"
                      showOnFocus={false}
                      container={baseDialogContainer ?? undefined}
                      tabIndex={9}
                    />
                  )}
                />
                {getFieldFeedback('birth')}
              </div>

              <div className="label-input-group">
                <Controller
                  name="ownerId"
                  control={control}
                  render={({ field }) => (
                    <AccountAutoComplete
                      label={t('account.accountRequired')}
                      field="name"
                      onSelect={(e: AutoCompleteSelectEvent) => {
                        field.onChange(e.value?.id);
                      }}
                      tabIndex={10}
                    />
                  )}
                />

                {getFieldFeedback('ownerId')}
              </div>

              <div className="label-input-group">
                <Controller
                  name="yardId"
                  control={control}
                  render={({ field }) => (
                    <YardsAutoComplete
                      label={t('donor.yardRequired')}
                      appendTo={null}
                      onSelect={(e: AutoCompleteSelectEvent) => {
                        field.onChange(e.value?.id);
                      }}
                      tabIndex={11}
                    />
                  )}
                />
                {getFieldFeedback('yardId')}
              </div>

              <div className="label-input-group">
                <label htmlFor="registrationNumber">
                  {t('fields.registrationNumber')}
                </label>
                <Controller
                  name="registrationNumber"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id={field.name}
                      value={field.value}
                      aria-invalid={isOverCharacterLimit('registrationNumber')}
                      onChange={(e) => field.onChange(e.target.value)}
                      tabIndex={12}
                    />
                  )}
                />
                {getFieldFeedback('registrationNumber')}
              </div>

              <div className="label-input-group">
                <label htmlFor="received">{t('donor.receivedDate')}</label>
                <Controller
                  name="received"
                  control={control}
                  render={({ field }) => (
                    <Calendar
                      id={field.name}
                      value={field.value}
                      onChange={(e) => field.onChange(e.value)}
                      showIcon
                      dateFormat="mm/dd/yy"
                      showOnFocus={false}
                      container={baseDialogContainer ?? undefined}
                      tabIndex={13}
                    />
                  )}
                />
                {getFieldFeedback('received')}
              </div>

              <div className="label-input-group">
                <label htmlFor="pregnant">{t('fields.pregnant')}</label>
                <Controller
                  name="pregnant"
                  control={control}
                  render={({ field }) => (
                    <UiCheckbox
                      id={field.name}
                      checked={field.value}
                      onCheckedChange={(checked) =>
                        field.onChange(checked === true)
                      }
                      tabIndex={14}
                    />
                  )}
                />
                {getFieldFeedback('pregnant')}
              </div>

              <div className="label-input-group">
                <Controller
                  name="ivfStatusId"
                  control={control}
                  render={({ field }) => (
                    <IvfStatusAutoComplete
                      label={t('ivf.ivfStatus')}
                      appendTo={null}
                      onSelect={(e: AutoCompleteSelectEvent) => {
                        field.onChange(e.value?.id);
                      }}
                      tabIndex={15}
                    />
                  )}
                />
                {getFieldFeedback('ivfStatusId')}
              </div>

              <div className="label-input-group">
                <label htmlFor="exportDonor">{t('export.exportDonor')}</label>
                <Controller
                  name="exportDonor"
                  control={control}
                  render={({ field }) => (
                    <UiCheckbox
                      id={field.name}
                      checked={field.value}
                      onCheckedChange={(checked) =>
                        field.onChange(checked === true)
                      }
                      tabIndex={16}
                    />
                  )}
                />
                {getFieldFeedback('exportDonor')}
              </div>

              <div className="label-input-group">
                <Controller
                  name="housingId"
                  control={control}
                  render={({ field }) => (
                    <HousingAutoComplete
                      label={t('donor.housing')}
                      appendTo={null}
                      onSelect={(e: AutoCompleteSelectEvent) => {
                        field.onChange(e.value?.id);
                      }}
                      tabIndex={17}
                    />
                  )}
                />
                {getFieldFeedback('housingId')}
              </div>
            </form>
          </DialogBody>
          <DialogFooter className="preflight">
            <UiButton
              type="button"
              variant="outline"
              disabled={isCreatingDonor}
              onClick={() => {
                reset();
                onHide();
              }}
            >
              {t('common.cancel')}
            </UiButton>
            <UiButton
              type="button"
              disabled={isCreatingDonor}
              onClick={handleSubmit(onSave, onInvalid)}
              title={
                isRevisionMode ? t('revision.revisionFieldChangeTooltip') : ''
              }
              className={
                isRevisionMode
                  ? 'bg-yellow-500 text-black hover:bg-yellow-500/80'
                  : ''
              }
            >
              {t('common.save')}
            </UiButton>
          </DialogFooter>
        </DialogContent>
      </UiDialog>
    );
  }

  return (
    <Dialog
      header={t('donor.newDonor')}
      visible={visible}
      onHide={onHide}
      footer={footerContent}
      modal
      className="p-fluid"
      style={{ maxWidth: '90vw', width: '1200px' }}
    >
      <form
        onSubmit={handleSubmit(onSave, onInvalid)}
        className="grid grid-cols-3 gap-4 p-4 [&_.p-calendar]:w-full [&_.p-checkbox]:mt-2 [&_.p-dropdown]:w-full [&_.p-inputtext]:w-full [&_[data-slot='field']]:m-0 [&_[data-slot='field']]:w-full"
        ref={formNavRef}
      >
        <div className="label-input-group">
          <label htmlFor="donorName">{t('donor.donorName')}</label>
          <Controller
            name="donorName"
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                value={field.value}
                className={classNames({
                  'p-invalid':
                    fieldState.invalid || isOverCharacterLimit('donorName'),
                })}
                onChange={(e) => field.onChange(e.target.value)}
                tabIndex={1}
              />
            )}
          />
          {getFieldFeedback('donorName')}
        </div>

        <SpeciesDropdown
          label={t('fields.species', 'Species')}
          tabIndex={2}
          onChange={(subFamilies) => {
            setSelectedSubFamilies(subFamilies);
            setValue('breedId', '');
          }}
        />

        <div className="label-input-group">
          <Controller
            name="breedId"
            control={control}
            render={({ field }) => (
              <BreedsAutoComplete
                key={selectedSubFamilies.join(',')}
                label={t('fields.breedRequired')}
                subFamilies={selectedSubFamilies}
                onSelect={(e: AutoCompleteSelectEvent) => {
                  field.onChange(e.value?.id);
                }}
                onChange={(e) => {
                  const value = e.value as unknown;
                  if (
                    typeof value === 'object' &&
                    value !== null &&
                    'id' in value &&
                    typeof value.id === 'string' &&
                    value.id.length > 0
                  ) {
                    field.onChange(value.id);
                    return;
                  }
                  field.onChange(undefined);
                }}
                tabIndex={3}
              />
            )}
          />
          {getFieldFeedback('breedId')}
        </div>

        <div className="label-input-group">
          <label htmlFor="pedName">{t('donor.pedigreeName')}</label>
          <Controller
            name="pedName"
            control={control}
            render={({ field }) => (
              <InputText
                id={field.name}
                value={field.value}
                className={classNames({
                  'p-invalid': isOverCharacterLimit('pedName'),
                })}
                onChange={(e) => field.onChange(e.target.value)}
                tabIndex={4}
              />
            )}
          />
          {getFieldFeedback('pedName')}
        </div>

        <div className="label-input-group">
          <label htmlFor="metalTag">{t('donor.metalTag')}</label>
          <Controller
            name="metalTag"
            control={control}
            render={({ field }) => (
              <InputText
                id={field.name}
                value={field.value}
                className={classNames({
                  'p-invalid': isOverCharacterLimit('metalTag'),
                })}
                onChange={(e) => field.onChange(e.target.value)}
                tabIndex={5}
              />
            )}
          />
          {getFieldFeedback('metalTag')}
        </div>

        <div className="label-input-group">
          <label htmlFor="lastActivity">{t('donor.lastActivity')}</label>
          <Controller
            name="lastActivity"
            control={control}
            render={({ field }) => (
              <InputText
                id={field.name}
                value={field.value}
                className={classNames({
                  'p-invalid': isOverCharacterLimit('lastActivity'),
                })}
                onChange={(e) => field.onChange(e.target.value)}
                tabIndex={6}
              />
            )}
          />
          {getFieldFeedback('lastActivity')}
        </div>

        <div className="label-input-group">
          <label htmlFor="tattoo">{t('donor.earTag')}</label>
          <Controller
            name="tattoo"
            control={control}
            render={({ field }) => (
              <InputText
                id={field.name}
                value={field.value}
                className={classNames({
                  'p-invalid': isOverCharacterLimit('tattoo'),
                })}
                onChange={(e) => field.onChange(e.target.value)}
                tabIndex={7}
              />
            )}
          />
          {getFieldFeedback('tattoo')}
        </div>

        <div className="label-input-group">
          <label htmlFor="regPrefix">{t('fields.country')}</label>
          <Controller
            name="regPrefix"
            control={control}
            render={({ field }) => (
              <InputText
                id={field.name}
                value={field.value}
                className={classNames({
                  'p-invalid': isOverCharacterLimit('regPrefix'),
                })}
                onChange={(e) => field.onChange(e.target.value)}
                tabIndex={8}
              />
            )}
          />
          {getFieldFeedback('regPrefix')}
        </div>

        <div className="label-input-group">
          <label htmlFor="birth">{t('fields.birthDate')}</label>
          <Controller
            name="birth"
            control={control}
            render={({ field }) => (
              <Calendar
                id={field.name}
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
                showIcon
                dateFormat="mm/dd/yy"
                showOnFocus={false}
                tabIndex={9}
              />
            )}
          />
          {getFieldFeedback('birth')}
        </div>

        <div className="label-input-group">
          <Controller
            name="ownerId"
            control={control}
            render={({ field }) => (
              <AccountAutoComplete
                label={t('account.accountRequired')}
                field="name"
                onSelect={(e: AutoCompleteSelectEvent) => {
                  field.onChange(e.value?.id);
                }}
                tabIndex={10}
              />
            )}
          />

          {getFieldFeedback('ownerId')}
        </div>

        <div className="label-input-group">
          <Controller
            name="yardId"
            control={control}
            render={({ field }) => (
              <YardsAutoComplete
                label={t('donor.yardRequired')}
                appendTo={null}
                onSelect={(e: AutoCompleteSelectEvent) => {
                  field.onChange(e.value?.id);
                }}
                tabIndex={11}
              />
            )}
          />
          {getFieldFeedback('yardId')}
        </div>

        <div className="label-input-group">
          <label htmlFor="registrationNumber">
            {t('fields.registrationNumber')}
          </label>
          <Controller
            name="registrationNumber"
            control={control}
            render={({ field }) => (
              <InputText
                id={field.name}
                value={field.value}
                className={classNames({
                  'p-invalid': isOverCharacterLimit('registrationNumber'),
                })}
                onChange={(e) => field.onChange(e.target.value)}
                tabIndex={12}
              />
            )}
          />
          {getFieldFeedback('registrationNumber')}
        </div>

        <div className="label-input-group">
          <label htmlFor="received">{t('donor.receivedDate')}</label>
          <Controller
            name="received"
            control={control}
            render={({ field }) => (
              <Calendar
                id={field.name}
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
                showIcon
                dateFormat="mm/dd/yy"
                showOnFocus={false}
                tabIndex={13}
              />
            )}
          />
          {getFieldFeedback('received')}
        </div>

        <div className="label-input-group">
          <label htmlFor="pregnant">{t('fields.pregnant')}</label>
          <Controller
            name="pregnant"
            control={control}
            render={({ field }) => (
              <Checkbox
                id={field.name}
                checked={field.value}
                onChange={(e) => field.onChange(e.checked)}
                tabIndex={14}
              />
            )}
          />
          {getFieldFeedback('pregnant')}
        </div>

        <div className="label-input-group">
          <Controller
            name="ivfStatusId"
            control={control}
            render={({ field }) => (
              <IvfStatusAutoComplete
                label={t('ivf.ivfStatus')}
                appendTo={null}
                onSelect={(e: AutoCompleteSelectEvent) => {
                  field.onChange(e.value?.id);
                }}
                tabIndex={15}
              />
            )}
          />
          {getFieldFeedback('ivfStatusId')}
        </div>

        <div className="label-input-group">
          <label htmlFor="exportDonor">{t('export.exportDonor')}</label>
          <Controller
            name="exportDonor"
            control={control}
            render={({ field }) => (
              <Checkbox
                id={field.name}
                checked={field.value}
                onChange={(e) => field.onChange(e.checked)}
                tabIndex={16}
              />
            )}
          />
          {getFieldFeedback('exportDonor')}
        </div>

        <div className="label-input-group">
          <Controller
            name="housingId"
            control={control}
            render={({ field }) => (
              <HousingAutoComplete
                label={t('donor.housing')}
                appendTo={null}
                onSelect={(e: AutoCompleteSelectEvent) => {
                  field.onChange(e.value?.id);
                }}
                tabIndex={17}
              />
            )}
          />
          {getFieldFeedback('housingId')}
        </div>
      </form>
    </Dialog>
  );
};
